import { IButtonStyles } from "@fluentui/react";
import {
  concatStyleSets,
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
  ITheme,
} from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import {
  ActionControlSemanticColors,
  ActionControlSize,
  getActionControlSemanticColorsWithTheme,
  getModernUxExperimentPreference,
  isTwoLineText,
} from "@1js/acui-common";

import {
  actionButtonStylingConstants,
  getSizedButtonStyles,
  SizedButtonStyles,
} from "../../styling";
import { getActionButtonStyles } from "../AppBaseButton";

export const splitIconClass = "ribbon-split-icon";
export const splitContainerClass = "ribbon-split-container";
export const splitTextClass = "ribbon-split-text";

const actionSplitButtonSizedStyles: SizedButtonStyles = {
  large: {
    splitButtonContainer: {
      height: 72,
      minWidth: 40,
      selectors: {
        ["> :first-child"]: {
          flexDirection: "column",
        },
      },
    },
    // root is splitPrimaryButton
    root: {
      height: 36,
      borderRight: "",
      borderBottom: 0,
      paddingBottom: 2,
    },
    splitButtonFlexContainer: {
      height: "100%",
      fontSize: 12,
      display: "block",
      lineHeight: "normal",
    },
    splitButtonMenuButton: {
      width: "100%",
      height: 36,
      borderLeft: "",
      borderTop: 0,
      padding: "0px 1px",
      selectors: {
        [HighContrastSelector]: {
          color: "ButtonText",
          selectors: {
            ":hover": {
              color: "HighlightText",
            },
          },
        },
        [`.${splitIconClass}`]: {
          width: 8,
          height: 18,
          padding: "0px 2px",
        },
        [`.${splitTextClass}`]: {
          lineHeight: 8,
        },
        [`.${splitContainerClass}`]: {
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          selectors: {
            [HighContrastSelector]: {
              ...getHighContrastNoAdjustStyle(),
              selectors: {
                ":hover": {
                  color: "HighlightText",
                  ...getHighContrastNoAdjustStyle(),
                },
              },
            },
          },
        },
      },
    },
    splitButtonContainerChecked: {
      selectors: {
        [`.${splitContainerClass}`]: {
          selectors: {
            [HighContrastSelector]: {
              color: "HighlightText",
              ...getHighContrastNoAdjustStyle(),
            },
          },
        },
      },
    },
    flexContainer: {
      height: 32,
      flexDirection: "column",
      justifyContent: "flex-start",
    },
  },
  small: {
    splitButtonContainer: {
      height: 24,
    },
    splitButtonMenuButton: {
      height: 24,
      width: 12,
    },
    root: {
      minWidth: 0,
      paddingRight: 2,
    },
    splitButtonMenuIcon: [
      actionButtonStylingConstants.defaultSizedControlStyles.small.menuIcon,
      {
        marginLeft: 2,
      },
    ],
  },
};

const actionSplitButtonTwoLineTextSizedStyles: SizedButtonStyles = {
  large: concatStyleSets(actionSplitButtonSizedStyles.large, {
    splitButtonMenuButton: {
      selectors: {
        [`.${splitTextClass}`]: {
          lineHeight: "normal",
          display: "flex",
          alignItems: "flex-end",
        },
        [`.${splitIconClass}`]: {
          height: 13,
        },
      },
    },
  }),
};

export const getSizedSplitButtonStyles = memoizeFunction(
  (
    label: string,
    size: ActionControlSize,
    hideLabel?: boolean,
    externalStyles?: IButtonStyles
  ): IButtonStyles => {
    const anchorStyles =
      size === ActionControlSize.Large && !hideLabel && isTwoLineText(label)
        ? actionSplitButtonTwoLineTextSizedStyles
        : actionSplitButtonSizedStyles;
    const sizedStyle = getSizedButtonStyles(size, externalStyles, anchorStyles);
    return sizedStyle ? sizedStyle : {};
  }
);

export const getActionSplitButtonStyles = memoizeFunction(
  (theme: ITheme, customStyles?: IButtonStyles): IButtonStyles => {
    // Adopt the default ribbon button styling for the split menu button
    const actionControlSemanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    const isModernUxEnabled = getModernUxExperimentPreference();
    const styles = getActionButtonStyles(theme);
    return concatStyleSets(
      styles,
      {
        root: {
          padding: "1px 2px 1px 9px",
        },
        label: {
          marginRight: 2,
        },
        splitButtonMenuButton: [
          styles.root,
          {
            width: 16,
            padding: 0,
            selectors: {
              [`.${splitIconClass}`]: {
                fill: actionControlSemanticColors.textColor,
              },
            },
          },
        ],
        splitButtonMenuButtonDisabled: styles.rootDisabled,
        splitButtonMenuButtonExpanded: styles.rootExpanded,
        splitButtonContainer: {
          height: 40,
          outline: "none",
          borderRadius: 0,
          selectors: {
            ":hover button": !isModernUxEnabled && {
              borderColor: actionControlSemanticColors.hoverBackgroundColor,
            },
            ":active button": !isModernUxEnabled && {
              borderColor: actionControlSemanticColors.pressedBackgroundColor,
            },
          },
        },
        splitButtonContainerChecked: {
          selectors: {
            button: !isModernUxEnabled && {
              borderColor: actionControlSemanticColors.toggledBackgroundColor,
            },
          },
        },
        splitButtonMenuIconDisabled: [
          {
            fill: actionControlSemanticColors.textDisabledColor,
          },
          !isModernUxEnabled && {
            // Opacity already applied in Modern UX "textDisabledColor"
            opacity: 0.4,
          },
        ],
        splitButtonDivider: { width: 0 },
        menuIcon: { marginLeft: 2 },
      },
      customStyles
    );
  }
);
